/**
 * Task #2903 — Catalogo SoT delle tipologie documento fiscale.
 *
 * Fonte di verità unica del dominio "emissione documenti": per ogni categoria
 * padre dichiara i figli TD ammessi, e per ogni codice TD i flag operativi
 * (campi anagrafici richiesti, blocchi XML FatturaPA attivati, listino,
 * magazzino, pagamento).
 *
 * Il catalogo dichiara la VALIDITÀ FISCALE (cosa esiste per l'AdE). Il SUPPORTO
 * del provider (oggi A-Cube) NON è un flag statico qui: è risolto a runtime dal
 * livello capability per-provider del backend.
 *
 * Riusa `FiscalDocumentCategory` (types/api.ts) come chiave categoria. Le icone
 * (componenti React) restano nel FE: qui esponiamo solo una `iconKey` stabile.
 */

import type { FiscalDocumentCategory } from '../types/api';
import type { TipoDocumentoCode } from './reference-tables';

// ---------------------------------------------------------------------------
// Enum di supporto
// ---------------------------------------------------------------------------

/** Direzione del movimento di magazzino indotto dal documento. */
export type MovimentoMagazzino = 'scarico' | 'carico' | null;

/** Campi anagrafici della controparte richiesti per la tipologia. */
export const ANAGRAFICA_FIELDS = [
  'denominazione',        // ragione sociale o nome+cognome
  'partita_iva',
  'codice_fiscale',
  'indirizzo',
  'cap',
  'comune',
  'provincia',
  'nazione',
  'codice_destinatario',  // CodiceDestinatario SDI (7 char privati, 6 PA)
  'pec',
  'regime_fiscale',
] as const;
export type AnagraficaField = typeof ANAGRAFICA_FIELDS[number];

/**
 * Blocchi del tracciato FatturaPA potenzialmente attivati dalla tipologia o
 * dai profili. Sottoinsieme rilevante per il dominio emissione (non l'intero
 * schema XSD).
 */
export const FATTURAPA_BLOCKS = [
  'DatiGeneraliDocumento',
  'DatiBeniServizi',
  'DatiRiepilogo',
  'CedentePrestatore',
  'CessionarioCommittente',
  'DatiPagamento',
  'DatiRitenuta',
  'DatiCassaPrevidenziale',
  'DatiBollo',
  'DatiFattureCollegate',
  'DatiDDT',
  'DatiOrdineAcquisto',
  'DatiContratto',
  'DatiSAL',
  'ScontoMaggiorazione',
  'Allegati',
] as const;
export type FatturaPaBlock = typeof FATTURAPA_BLOCKS[number];

// ---------------------------------------------------------------------------
// Descrittore per categoria (padre)
// ---------------------------------------------------------------------------

export interface FiscalCategoryDescriptor {
  readonly category: FiscalDocumentCategory;
  readonly label: string;
  readonly description: string;
  /** Chiave icona stabile; il FE la mappa al componente lucide. */
  readonly iconKey:
    | 'fattura' | 'nota_credito' | 'nota_debito' | 'autofattura'
    | 'parcella' | 'ricevuta' | 'credito_patrimoniale';
  /** True se la categoria è trasmessa allo SDI (esclude i gestionali). */
  readonly sdi: boolean;
  /**
   * True se la categoria ammette i blocchi fiscali documentali opzionali
   * (DatiBollo, DatiRitenuta, DatiCassaPrevidenziale). Escluse le autofatture
   * reverse-charge (no pagamento/ritenute) e i gestionali non-SDI. Capability
   * dichiarativa SoT consumata dal FE per il gating della sezione "Dati Fiscali".
   */
  readonly admitsFiscalBlocks: boolean;
  /** Codici TD ammessi sotto questa categoria (vuoto per i gestionali). */
  readonly children: ReadonlyArray<TipoDocumentoCode>;
}

export const FISCAL_CATEGORY_CATALOG: Readonly<Record<FiscalDocumentCategory, FiscalCategoryDescriptor>> = {
  fattura: {
    category: 'fattura',
    label: 'Fattura',
    description: 'Documento di vendita trasmesso a SDI.',
    iconKey: 'fattura',
    sdi: true,
    admitsFiscalBlocks: true,
    children: ['TD01', 'TD02', 'TD07', 'TD24', 'TD25', 'TD26'],
  },
  nota_credito: {
    category: 'nota_credito',
    label: 'Nota di credito',
    description: 'Storno totale o parziale di una fattura precedente.',
    iconKey: 'nota_credito',
    sdi: true,
    admitsFiscalBlocks: true,
    children: ['TD04', 'TD08'],
  },
  nota_debito: {
    category: 'nota_debito',
    label: 'Nota di debito',
    description: 'Addebito integrativo su una fattura precedente.',
    iconKey: 'nota_debito',
    sdi: true,
    admitsFiscalBlocks: true,
    children: ['TD05', 'TD09'],
  },
  autofattura: {
    category: 'autofattura',
    label: 'Autofattura',
    description: 'Reverse charge / autofattura / integrazione.',
    iconKey: 'autofattura',
    sdi: true,
    admitsFiscalBlocks: false,
    children: ['TD16', 'TD17', 'TD18', 'TD19', 'TD20', 'TD21', 'TD22', 'TD23', 'TD27', 'TD28'],
  },
  parcella: {
    category: 'parcella',
    label: 'Parcella',
    description: 'Parcella professionale.',
    iconKey: 'parcella',
    sdi: true,
    admitsFiscalBlocks: true,
    children: ['TD06', 'TD03'],
  },
  ricevuta: {
    category: 'ricevuta',
    label: 'Ricevuta',
    description: 'Ricevuta semplice (gestionale, non SDI).',
    iconKey: 'ricevuta',
    sdi: false,
    admitsFiscalBlocks: false,
    children: [],
  },
  credito_patrimoniale: {
    category: 'credito_patrimoniale',
    label: 'Credito patrimoniale',
    description: 'Voce di patrimonio netto / finanziamento (gestionale).',
    iconKey: 'credito_patrimoniale',
    sdi: false,
    admitsFiscalBlocks: false,
    children: [],
  },
};

/** Categorie trasmesse a SDI (derivata dal catalogo, no liste hardcoded). */
export const SDI_CATEGORIES: ReadonlyArray<FiscalDocumentCategory> =
  (Object.values(FISCAL_CATEGORY_CATALOG) as ReadonlyArray<FiscalCategoryDescriptor>)
    .filter((c) => c.sdi)
    .map((c) => c.category);

/**
 * True se la tipologia documento ammette i blocchi fiscali documentali
 * opzionali (Bollo, Ritenuta d'acconto, ENASARCO, Cassa Previdenziale).
 * Capability dichiarativa derivata dalla categoria del catalogo SoT: il FE la
 * usa per il gating della sezione "Dati Fiscali" (no liste TD hardcoded in UI).
 */
export function tdAdmitsFiscalBlocks(td: TipoDocumentoCode): boolean {
  const descriptor = FISCAL_DOCUMENT_TYPES[td];
  if (!descriptor) return false;
  return FISCAL_CATEGORY_CATALOG[descriptor.category].admitsFiscalBlocks;
}

// ---------------------------------------------------------------------------
// Descrittore per tipologia (codice TD)
// ---------------------------------------------------------------------------

export interface FiscalDocumentTypeDescriptor {
  readonly td: TipoDocumentoCode;
  readonly category: FiscalDocumentCategory;
  readonly label: string;
  readonly description: string;
  readonly requiredAnagrafica: ReadonlyArray<AnagraficaField>;
  readonly xmlBlocks: ReadonlyArray<FatturaPaBlock>;
  readonly richiede_listino: boolean;
  readonly richiede_magazzino: boolean;
  /**
   * Movimento magazzino per la tipologia. Per NC/ND il movimento è guidato
   * dalla causale (vedi nc-nd-causali.ts): qui resta `null`.
   */
  readonly movimento_magazzino: MovimentoMagazzino;
  readonly prevede_pagamento: boolean;
}

// Anagrafica comune B2B/SDI (denominazione, P.IVA o CF, indirizzo, codice
// destinatario o PEC). Riusata dalla maggior parte delle tipologie SDI.
const ANAG_SDI: ReadonlyArray<AnagraficaField> = [
  'denominazione', 'partita_iva', 'codice_fiscale', 'indirizzo', 'cap',
  'comune', 'provincia', 'nazione', 'codice_destinatario', 'pec',
];

// Blocchi base di ogni fattura/nota SDI.
const XML_BASE: ReadonlyArray<FatturaPaBlock> = [
  'DatiGeneraliDocumento', 'DatiBeniServizi', 'DatiRiepilogo',
  'CedentePrestatore', 'CessionarioCommittente',
];

const XML_BASE_PAGAMENTO: ReadonlyArray<FatturaPaBlock> = [...XML_BASE, 'DatiPagamento'];
const XML_BASE_COLLEGATE: ReadonlyArray<FatturaPaBlock> = [...XML_BASE, 'DatiFattureCollegate'];

export const FISCAL_DOCUMENT_TYPES: Readonly<Record<TipoDocumentoCode, FiscalDocumentTypeDescriptor>> = {
  // ---- Fattura ----
  TD01: {
    td: 'TD01', category: 'fattura', label: 'Fattura', description: 'Fattura ordinaria.',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: XML_BASE_PAGAMENTO,
    richiede_listino: true, richiede_magazzino: true, movimento_magazzino: 'scarico', prevede_pagamento: true,
  },
  TD02: {
    td: 'TD02', category: 'fattura', label: 'Acconto/Anticipo su fattura', description: 'Acconto o anticipo su fattura.',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: XML_BASE_PAGAMENTO,
    richiede_listino: false, richiede_magazzino: false, movimento_magazzino: null, prevede_pagamento: true,
  },
  TD07: {
    td: 'TD07', category: 'fattura', label: 'Fattura semplificata', description: 'Fattura semplificata (art. 21-bis DPR 633/72).',
    requiredAnagrafica: ['denominazione', 'partita_iva', 'codice_fiscale'], xmlBlocks: XML_BASE,
    richiede_listino: true, richiede_magazzino: false, movimento_magazzino: null, prevede_pagamento: true,
  },
  TD24: {
    td: 'TD24', category: 'fattura', label: 'Fattura differita art.21 c.4 lett.a)', description: 'Fattura differita di cui all’art.21 c.4 lett.a).',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: [...XML_BASE_PAGAMENTO, 'DatiDDT'],
    richiede_listino: true, richiede_magazzino: true, movimento_magazzino: 'scarico', prevede_pagamento: true,
  },
  TD25: {
    td: 'TD25', category: 'fattura', label: 'Fattura differita art.21 c.4 lett.b)', description: 'Fattura differita di cui all’art.21 c.4 terzo periodo lett.b).',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: [...XML_BASE_PAGAMENTO, 'DatiDDT'],
    richiede_listino: true, richiede_magazzino: true, movimento_magazzino: 'scarico', prevede_pagamento: true,
  },
  TD26: {
    td: 'TD26', category: 'fattura', label: 'Cessione beni ammortizzabili e passaggi interni', description: 'Cessione di beni ammortizzabili e per passaggi interni (ex art.36 DPR 633/72).',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: XML_BASE,
    richiede_listino: false, richiede_magazzino: true, movimento_magazzino: 'scarico', prevede_pagamento: false,
  },

  // ---- Nota di credito ----
  TD04: {
    td: 'TD04', category: 'nota_credito', label: 'Nota di credito', description: 'Nota di credito (variazione in diminuzione).',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: XML_BASE_COLLEGATE,
    richiede_listino: false, richiede_magazzino: false, movimento_magazzino: null, prevede_pagamento: false,
  },
  TD08: {
    td: 'TD08', category: 'nota_credito', label: 'Nota di credito semplificata', description: 'Nota di credito semplificata.',
    requiredAnagrafica: ['denominazione', 'partita_iva', 'codice_fiscale'], xmlBlocks: XML_BASE_COLLEGATE,
    richiede_listino: false, richiede_magazzino: false, movimento_magazzino: null, prevede_pagamento: false,
  },

  // ---- Nota di debito ----
  TD05: {
    td: 'TD05', category: 'nota_debito', label: 'Nota di debito', description: 'Nota di debito (variazione in aumento).',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: XML_BASE_COLLEGATE,
    richiede_listino: false, richiede_magazzino: false, movimento_magazzino: null, prevede_pagamento: true,
  },
  TD09: {
    td: 'TD09', category: 'nota_debito', label: 'Nota di debito semplificata', description: 'Nota di debito semplificata.',
    requiredAnagrafica: ['denominazione', 'partita_iva', 'codice_fiscale'], xmlBlocks: XML_BASE_COLLEGATE,
    richiede_listino: false, richiede_magazzino: false, movimento_magazzino: null, prevede_pagamento: true,
  },

  // ---- Parcella ----
  TD06: {
    td: 'TD06', category: 'parcella', label: 'Parcella', description: 'Parcella professionale.',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: [...XML_BASE_PAGAMENTO, 'DatiRitenuta', 'DatiCassaPrevidenziale'],
    richiede_listino: false, richiede_magazzino: false, movimento_magazzino: null, prevede_pagamento: true,
  },
  TD03: {
    td: 'TD03', category: 'parcella', label: 'Acconto/Anticipo su parcella', description: 'Acconto o anticipo su parcella.',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: [...XML_BASE_PAGAMENTO, 'DatiRitenuta', 'DatiCassaPrevidenziale'],
    richiede_listino: false, richiede_magazzino: false, movimento_magazzino: null, prevede_pagamento: true,
  },

  // ---- Autofattura / integrazione ----
  TD16: {
    td: 'TD16', category: 'autofattura', label: 'Integrazione reverse charge interno', description: 'Integrazione fattura reverse charge interno.',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: XML_BASE,
    richiede_listino: false, richiede_magazzino: false, movimento_magazzino: null, prevede_pagamento: false,
  },
  TD17: {
    td: 'TD17', category: 'autofattura', label: 'Integrazione/autofattura servizi estero', description: 'Integrazione/autofattura per acquisto servizi dall’estero.',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: XML_BASE,
    richiede_listino: false, richiede_magazzino: false, movimento_magazzino: null, prevede_pagamento: false,
  },
  TD18: {
    td: 'TD18', category: 'autofattura', label: 'Integrazione beni intracomunitari', description: 'Integrazione per acquisto di beni intracomunitari.',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: XML_BASE,
    richiede_listino: false, richiede_magazzino: false, movimento_magazzino: null, prevede_pagamento: false,
  },
  TD19: {
    td: 'TD19', category: 'autofattura', label: 'Integrazione/autofattura art.17 c.2', description: 'Integrazione/autofattura per acquisto di beni ex art.17 c.2 DPR 633/72.',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: XML_BASE,
    richiede_listino: false, richiede_magazzino: false, movimento_magazzino: null, prevede_pagamento: false,
  },
  TD20: {
    td: 'TD20', category: 'autofattura', label: 'Autofattura regolarizzazione', description: 'Autofattura per regolarizzazione e integrazione delle fatture (art.6 c.8 d.lgs.471/97 o art.46 c.5 DL 331/93).',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: XML_BASE,
    richiede_listino: false, richiede_magazzino: false, movimento_magazzino: null, prevede_pagamento: false,
  },
  TD21: {
    td: 'TD21', category: 'autofattura', label: 'Autofattura per splafonamento', description: 'Autofattura per splafonamento.',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: XML_BASE,
    richiede_listino: false, richiede_magazzino: false, movimento_magazzino: null, prevede_pagamento: false,
  },
  TD22: {
    td: 'TD22', category: 'autofattura', label: 'Estrazione beni da Deposito IVA', description: 'Estrazione beni da Deposito IVA.',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: XML_BASE,
    richiede_listino: false, richiede_magazzino: true, movimento_magazzino: 'carico', prevede_pagamento: false,
  },
  TD23: {
    td: 'TD23', category: 'autofattura', label: 'Estrazione beni da Deposito IVA con versamento IVA', description: 'Estrazione beni da Deposito IVA con versamento dell’IVA.',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: XML_BASE,
    richiede_listino: false, richiede_magazzino: true, movimento_magazzino: 'carico', prevede_pagamento: false,
  },
  TD27: {
    td: 'TD27', category: 'autofattura', label: 'Fattura per autoconsumo o cessioni gratuite', description: 'Fattura per autoconsumo o per cessioni gratuite senza rivalsa.',
    requiredAnagrafica: ['denominazione', 'partita_iva', 'codice_fiscale'], xmlBlocks: XML_BASE,
    richiede_listino: false, richiede_magazzino: true, movimento_magazzino: 'scarico', prevede_pagamento: false,
  },
  TD28: {
    td: 'TD28', category: 'autofattura', label: 'Acquisti da San Marino con IVA (cartacea)', description: 'Acquisti da San Marino con IVA (fattura cartacea).',
    requiredAnagrafica: ANAG_SDI, xmlBlocks: XML_BASE,
    richiede_listino: false, richiede_magazzino: true, movimento_magazzino: 'carico', prevede_pagamento: false,
  },
};

// ---------------------------------------------------------------------------
// Profili / varianti della Fattura (NON nuovi TD)
// ---------------------------------------------------------------------------

export const FATTURA_PROFILE_IDS = ['pubblica_amministrazione', 'ritenuta_acconto'] as const;
export type FatturaProfileId = typeof FATTURA_PROFILE_IDS[number];

export interface FatturaProfile {
  readonly id: FatturaProfileId;
  readonly label: string;
  readonly description: string;
  /** Valori forzati sul tracciato quando il profilo è attivo. */
  readonly forces: {
    readonly formatoTrasmissione?: 'FPA12' | 'FPR12';
    readonly codiceDestinatarioLength?: number;
    readonly esigibilitaIva?: EsigibilitaIvaCodeForProfile;
  };
  /** Blocchi XML aggiuntivi attivati dal profilo. */
  readonly activatesXmlBlocks: ReadonlyArray<FatturaPaBlock>;
  /** Campi anagrafici aggiuntivi richiesti dal profilo. */
  readonly requiredAnagrafica: ReadonlyArray<AnagraficaField>;
}

// Tipo locale ristretto allo split-payment usato dai profili (evita import
// ciclico: l'enum completo vive in reference-tables.ts).
type EsigibilitaIvaCodeForProfile = 'I' | 'D' | 'S';

export const FATTURA_PROFILES: Readonly<Record<FatturaProfileId, FatturaProfile>> = {
  pubblica_amministrazione: {
    id: 'pubblica_amministrazione',
    label: 'Pubblica Amministrazione',
    description: 'Destinatario PA: FormatoTrasmissione FPA12, CodiceDestinatario a 6 caratteri, CIG/CUP obbligatori, split-payment (esigibilità "S").',
    forces: {
      formatoTrasmissione: 'FPA12',
      codiceDestinatarioLength: 6,
      esigibilitaIva: 'S',
    },
    activatesXmlBlocks: ['DatiOrdineAcquisto', 'DatiContratto'],
    requiredAnagrafica: ['codice_destinatario'],
  },
  ritenuta_acconto: {
    id: 'ritenuta_acconto',
    label: 'Con ritenuta d’acconto',
    description: 'Attiva il blocco DatiRitenuta (tipo ritenuta, importo, aliquota, causale pagamento).',
    forces: {},
    activatesXmlBlocks: ['DatiRitenuta'],
    requiredAnagrafica: ['codice_fiscale'],
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Categoria padre di un codice TD (undefined se sconosciuto). */
export function categoryOfTd(td: TipoDocumentoCode): FiscalDocumentCategory {
  return FISCAL_DOCUMENT_TYPES[td].category;
}

/** True se il TD richiede un documento collegato (DatiFattureCollegate). */
export function tdRequiresRelated(td: string): boolean {
  return td === 'TD04' || td === 'TD05' || td === 'TD08' || td === 'TD09';
}

/** True se il TD è una fattura differita (richiede DatiDDT valorizzato). */
export function tdIsDifferita(td: string): boolean {
  return td === 'TD24' || td === 'TD25';
}
